import { type FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { toErrorKey } from '../lib/error-code.js';
import { Button } from '../ui/kit.js';
import { AuthCard } from './login-page.js';
import { authApi, sessionManager } from './session.js';

/**
 * Registration creates the account and the family together.
 *
 * A parent without a family cannot do anything in this product, so the contract
 * makes them one step rather than a state this screen would have to explain.
 *
 * The time zone is taken from the browser and shown rather than asked for. It
 * is not decoration: the daily budget's "day" is the family's local calendar
 * day (`SPEC.md §19.2`), so without it the day has no defined start.
 */
export const RegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const id = useId();

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setErrorKey(null);
    setIsSubmitting(true);

    try {
      const { session } = await authApi.register({
        email,
        password,
        displayName,
        familyName,
        timeZone,
      });
      sessionManager.adopt(session);
      void navigate('/', { replace: true });
    } catch (error) {
      setErrorKey(toErrorKey(error));
      setIsSubmitting(false);
    }
  };

  return (
    <AuthCard
      title={t('auth.registerTitle')}
      subtitle={t('auth.registerSubtitle')}
      onSubmit={(event) => void submit(event)}
      footer={
        <>
          {t('auth.haveAccount')} <Link to="/login">{t('auth.signIn')}</Link>
        </>
      }
    >
      <div className="field">
        <label htmlFor={`${id}-name`}>{t('auth.displayName')}</label>
        <input
          id={`${id}-name`}
          className="input"
          type="text"
          autoComplete="name"
          required
          maxLength={80}
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor={`${id}-family`}>{t('auth.familyName')}</label>
        <input
          id={`${id}-family`}
          className="input"
          type="text"
          required
          maxLength={80}
          value={familyName}
          onChange={(event) => setFamilyName(event.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor={`${id}-email`}>{t('auth.email')}</label>
        <input
          id={`${id}-email`}
          className="input"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor={`${id}-password`}>{t('auth.password')}</label>
        <input
          id={`${id}-password`}
          className="input"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          aria-describedby={`${id}-password-help`}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <p className="help" id={`${id}-password-help`}>
          {t('auth.passwordHint')}
        </p>
      </div>

      <p className="t-meta auth-note">{t('auth.timeZoneNote', { timeZone })}</p>

      {errorKey === null ? null : (
        <p className="help err" role="alert">
          {t(errorKey)}
        </p>
      )}

      <Button type="submit" variant="primary" block disabled={isSubmitting}>
        {t(isSubmitting ? 'auth.registering' : 'auth.register')}
      </Button>
    </AuthCard>
  );
};
